import { useNavigate } from "react-router-dom";
import { useState, useMemo, useEffect } from "react";
import { useTable, useSortBy, usePagination } from "react-table";
import { VscEye } from "react-icons/vsc";
import { Tooltip } from "antd";
import { Pencil, X, Info } from "lucide-react";
import moment from "moment";

import { CommonImages, ProfileImages } from "../../../../reusableUI/images";
import Loader from "../../../../reusableUI/loader";
import ReusableTable from "../../../../reusableUI/table";
import Page from "../../../../reusableUI/page";

import { capitalizeFirstLetter } from "../../../../utils/Functions";
import {
    getUnassignedHCPNames,
    getBookingsDetails,
} from "../../../../Service/bookings";
import { getHospitalList } from "../../../../Service/RM";

const SortIcon = ({ isSorted, isSortedDesc }) => {
    if (!isSorted) {
        return (
            <img
                src={ProfileImages.filter}
                alt="filter"
                className="h-3 w-2 ml-1 cursor-pointer opacity-60"
            />
        );
    }

    if (isSortedDesc) {
        return (
            <svg
                className="h-3 w-3 ml-1 text-blue-600"
                fill="currentColor"
                viewBox="0 0 20 20"
            >
                <path d="M5 8l5 5 5-5H5z" />
            </svg>
        );
    }

    return (
        <svg
            className="h-3 w-3 ml-1 text-blue-600"
            fill="currentColor"
            viewBox="0 0 20 20"
        >
            <path d="M15 12l-5-5-5 5h10z" />
        </svg>
    );
};

const CancelledBookings = ({ searchTerm }) => {
    const [loading, setLoading] = useState(true);
    const [paginationPage, setPaginationPage] = useState(0);
    const [unassignedList, setUnassignedList] = useState([]);
    const [hcpNames, setUnassignedHCPNames] = useState([]);
    const [filterType, setFilterType] = useState("All");
    const [selectedPageCount, setSelectedPageCount] = useState(() => {
        const saved = localStorage.getItem("pageCount");
        return saved ? parseInt(saved, 10) : 50;
    });
    const [sortBy, setSortBy] = useState(() => {
        const saved = localStorage.getItem("sortBy");
        return saved ? JSON.parse(saved) : [];
    });

    const navigate = useNavigate();

    useEffect(() => {
        fetchBookings();
    }, []);

    const fetchBookings = async () => {
        try {
            setLoading(true);
            const result = await getBookingsDetails();
            console.log("Unassigned Bookings fetched:", result);
            const bookingsData = Array.isArray(result?.data?.bookings)
                ? result.data.bookings
                : [];
            const cancelledBookings = bookingsData.filter(
                (booking) => booking?.assigned === true && booking?.status === "cancelled" || booking?.status === "expired" || booking?.status === "not paid"
            );
            setUnassignedList(cancelledBookings);
        } catch (error) {
            console.error("Error fetching bookings:", error);
            setUnassignedList([]);
        } finally {
            setLoading(false);
        }
    };

    // Fetch HCP names
    useEffect(() => {
        const fetchHCPNames = async () => {
            try {
                const response = await getHospitalList();
                console.log(response)
                if (response?.data && Array.isArray(response.data)) {
                    setUnassignedHCPNames(response.data);
                } else if (Array.isArray(response)) {
                    setUnassignedHCPNames(response);
                } else {
                    setUnassignedHCPNames([]);
                }
            } catch (error) {
                console.error("Error fetching Hospital names:", error);
                setUnassignedHCPNames([]);
            }
        };
        fetchHCPNames();
    }, []);

    // Fetch bookings on component mount

    const navigateToProfile = (index, original) => {
        localStorage.setItem("profilePage", "user");
        navigate(`/admin/bookingsProfile/${original._id}`, {
            state: { user: original, card: "2" },
        });
    };

    // Filtered data
    const filteredData = useMemo(() => {
        let list = unassignedList;
        if (filterType === "Individual") {
            list = list.filter((item) => item?.appointmentType?.type === "self");
        } else if (filterType === "Partner") {
            list = list.filter((item) => item?.appointmentType?.type === "partner");
        }

        if (!searchTerm) return list;
        return list.filter((item) =>
            item?.patient?.name?.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [unassignedList, searchTerm, filterType]);

    const calculateAge = (dob) => {
        if (!dob) return "-";
        const age = moment().diff(moment(dob), "years");
        return `${age} yrs`;
    };

    // Table columns
    const columns = useMemo(() => {
        return [
            {
                Header: () => <div className="font-poppins whitespace-nowrap text-[13px]">Name</div>,
                accessor: "patient.name",
                id: "name",
                width: 200,
                minWidth: 180,
                maxWidth: 240,
                Cell: ({ row: { original } }) => (
                    <div className="flex flex-row items-center gap-1.5 whitespace-nowrap ml-2">
                        <div className="relative inline-block shrink-0">
                            <img
                                src={
                                    original?.patient?.gender?.toLowerCase() === "female"
                                        ? CommonImages.female
                                        : original?.patient?.gender?.toLowerCase() === "male"
                                            ? CommonImages.male
                                            : CommonImages.defaultDp
                                }
                                alt="Profile"
                                className="h-8 w-8 rounded-full object-cover"
                            />
                        </div>
                        <span className="text-[#4361EE] font-poppins text-[13px] font-medium">
                            {capitalizeFirstLetter(original?.patient?.name || "N/A")}
                        </span>
                    </div>
                ),
            },
            {
                Header: () => <div className="font-poppins whitespace-nowrap text-[13px]">Date</div>,
                accessor: "visitInfo",
                width: 170,
                minWidth: 150,
                maxWidth: 200,
                Cell: ({ row: { original } }) => {
                    const start = original?.visitInfo?.startDate ? moment(original.visitInfo.startDate).format("DD/MM/YYYY") : "";
                    const end = original?.visitInfo?.endDate ? moment(original.visitInfo.endDate).format("DD/MM/YYYY") : "";
                    const days = parseInt(original?.visitInfo?.days) || 0;

                    let dateText = "-";
                    if (days === 1 || start === end) {
                        dateText = start;
                    } else if (start && end) {
                        dateText = `${start} - ${end}`;
                    } else if (start || end) {
                        dateText = start || end;
                    }

                    return (
                        <div className="font-poppins text-[13px] text-gray-700 whitespace-nowrap">
                            {dateText}
                        </div>
                    );
                },
            },
            {
                Header: () => <div className="font-poppins whitespace-nowrap text-[13px]">Time</div>,
                accessor: "visitInfo.slot",
                width: 160,
                minWidth: 140,
                maxWidth: 190,
                Cell: ({ row: { original } }) => {
                    const timeText = original?.visitInfo?.slot || "-";
                    return (
                        <div className="font-poppins text-[13px] text-gray-700 whitespace-nowrap">
                            {timeText}
                        </div>
                    );
                },
            },
            {
                Header: () => <div className="font-poppins whitespace-nowrap text-[13px]">Gender</div>,
                accessor: "patient.gender",
                width: 90,
                minWidth: 80,
                maxWidth: 110,
                Cell: ({ row: { original } }) => (
                    <div className="font-poppins text-[13px] text-gray-700">
                        {capitalizeFirstLetter(original?.patient?.gender || "-")}
                    </div>
                ),
            },
            // {
            //     Header: () => <div className="font-poppins whitespace-nowrap text-[13px]">Age</div>,
            //     accessor: "patient.dob",
            //     width: 80,
            //     minWidth: 70,
            //     maxWidth: 100,
            //     Cell: ({ row: { original } }) => (
            //         <div className="font-poppins text-[13px] text-gray-700 whitespace-nowrap">
            //             {calculateAge(original?.patient?.dob || null)}
            //         </div>
            //     ),
            // },
            {
                Header: () => <div className="font-poppins whitespace-nowrap text-[13px]">Service</div>,
                accessor: "specialistInfo.role",
                width: 140,
                minWidth: 120,
                maxWidth: 160,
                Cell: ({ row: { original } }) => {
                    let roleStr = capitalizeFirstLetter(original?.specialistInfo?.role || "-");
                    if (roleStr.toLowerCase() === "physio" || roleStr.toLowerCase() === "physiotherapist") {
                        roleStr = "Physiotherapist";
                    }
                    return (
                        <div className="font-poppins text-[13px] text-gray-700">
                            {roleStr}
                        </div>
                    );
                },
            },
            {
                Header: () => <div className="font-poppins whitespace-nowrap text-[13px]">Location</div>,
                accessor: "visitInfo.addressInfo.district",
                width: 200,
                minWidth: 180,
                maxWidth: 250,
                Cell: ({ row: { original } }) => {

                    const city = original?.visitInfo?.addressInfo?.city || "";
                    const loc = [city].filter(Boolean).map(capitalizeFirstLetter).join(", ");
                    return (
                        <div className="font-poppins text-[13px] text-gray-700 whitespace-normal break-words max-w-[230px]" title={loc || "-"}>
                            {loc || "-"}
                        </div>
                    );
                },
            },
            {
                Header: () => <div className="font-poppins whitespace-nowrap text-[13px]">Assigned to</div>,
                accessor: "assignedTo",
                width: 160,
                minWidth: 140,
                maxWidth: 180,
                Cell: ({ row: { original } }) => {
                    let displayAssignee = "-";
                    if (original?.appointmentType?.type === "partner") {
                        displayAssignee = original?.appointmentType?.partnerName || original?.assignedTo || "-";
                    } else if (original?.appointmentType?.type === "self") {
                        displayAssignee = original?.specialistInfo?.name || original?.assignedTo || "-";
                    } else {
                        displayAssignee = original?.assignedTo || "-";
                    }

                    return (
                        <div className="font-poppins text-[13px] flex items-center gap-1.5 min-w-[160px]">
                            <span
                                className={`truncate max-w-[130px] ${displayAssignee !== "-" ? "text-[#4361EE] font-medium" : "text-gray-500"}`}
                            >
                                {displayAssignee}
                            </span>
                            {displayAssignee !== "-" && displayAssignee.length > 10 && (
                                <Tooltip title={displayAssignee} placement="top">
                                    <div className="cursor-pointer text-gray-400 hover:text-blue-600 shrink-0 mt-0.5">
                                        <Info size={14} />
                                    </div>
                                </Tooltip>
                            )}
                        </div>
                    );
                },
            },
            {
                Header: () => <div className="font-poppins whitespace-nowrap text-[13px]">Status</div>,
                accessor: "status",
                width: 110,
                minWidth: 100,
                maxWidth: 130,
                Cell: ({ row: { original } }) => (
                    <div className="font-poppins text-[13px] text-[#00A86B] font-medium whitespace-nowrap">
                        {capitalizeFirstLetter(original?.status) || "Cancelled"}
                    </div>
                ),
            },
            {
                Header: () => <div className="font-poppins whitespace-nowrap text-[13px]">Profile</div>,
                accessor: "view",
                width: 80,
                minWidth: 70,
                maxWidth: 90,
                Cell: ({ row: { original } }) => (
                    <div className="flex flex-row items-center">
                        <button
                            onClick={() => navigateToProfile(null, original)}
                            className="text-[#4361EE] underline font-medium font-poppins text-[13px] hover:opacity-80"
                        >
                            View
                        </button>
                    </div>
                ),
            },
        ];
    }, [hcpNames, paginationPage, selectedPageCount]);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        page: tablePage,
        prepareRow,
        nextPage,
        previousPage,
        canPreviousPage,
        canNextPage,
        pageOptions,
        state: { sortBy: currentSortBy },
    } = useTable(
        {
            columns,
            data: filteredData,
            initialState: {
                pageSize: selectedPageCount,
                pageIndex: paginationPage,
                sortBy: sortBy.length > 0 ? sortBy : [],
            },
        },
        useSortBy,
        usePagination
    );

    useEffect(() => {
        localStorage.setItem("sortBy", JSON.stringify(currentSortBy));
    }, [currentSortBy]);

    return (
        <>
            {loading ? (
                <Loader loading={loading} />
            ) : (
                <div className="bg-[#F3F6F5]">
                    <div className="overflow-x-auto bg-white rounded-2xl shadow-lg p-2 h-[89vh] m-2 flex flex-col gap-3 justify-between">
                        <div className="flex justify-between items-center pr-4">
                            <p className="text-[14px] ml-2 font-bold pb-2 p-2">
                                Cancelled Visits ({filteredData.length})
                            </p>
                            <div className="flex items-center bg-white border border-blue-600 rounded-md px-2 h-[32px]" style={{ borderWidth: 1.5 }}>
                                <select
                                    value={filterType}
                                    onChange={(e) => setFilterType(e.target.value)}
                                    className="outline-none font-poppins text-[12px] text-blue-600 bg-transparent font-medium cursor-pointer"
                                >
                                    <option value="All">All</option>
                                    <option value="Individual">Individual</option>
                                    <option value="Partner">Partner</option>
                                </select>
                            </div>
                        </div>

                        <div className="overflow-x-auto min-h-[75vh] max-h-[75vh] mt-[-10px]">
                            <div className="min-w-[1200px] w-full h-full">
                                <table {...getTableProps()} className="w-full">
                                    <thead className="bg-[#F6F8FC] sticky top-0 z-10">
                                        {headerGroups.map((headerGroup) => (
                                            <tr key={headerGroup.id} {...headerGroup.getHeaderGroupProps()}>
                                                {headerGroup.headers.map((column) => (
                                                    <th
                                                        key={column.id}
                                                        {...column.getHeaderProps(column.getSortByToggleProps())}
                                                        className="px-2 py-1.5 text-left text-[12px] font-medium text-[#8177A8] tracking-wider cursor-pointer hover:bg-gray-100 whitespace-nowrap"
                                                    >
                                                        <div className="flex items-center gap-1">
                                                            {column.render("Header")}
                                                            {column.canSort && (
                                                                <SortIcon isSorted={column.isSorted} isSortedDesc={column.isSortedDesc} />
                                                            )}
                                                        </div>
                                                    </th>
                                                ))}
                                            </tr>
                                        ))}
                                    </thead>
                                    <tbody {...getTableBodyProps()} className="bg-white divide-y divide-gray-200">
                                        {tablePage.map((row) => {
                                            prepareRow(row);
                                            return (
                                                <tr
                                                    key={row.id}
                                                    {...row.getRowProps()}
                                                    className="hover:bg-gray-50 transition-colors"
                                                >
                                                    {row.cells.map((cell) => (
                                                        <td
                                                            key={cell.column.id}
                                                            {...cell.getCellProps()}
                                                            className="px-2 py-1.5 text-[12px] text-gray-700 whitespace-nowrap"
                                                        >
                                                            {cell.render("Cell")}
                                                        </td>
                                                    ))}
                                                </tr>
                                            );
                                        })}
                                        {tablePage.length === 0 && (
                                            <tr>
                                                <td colSpan={columns.length} className="text-center py-10 text-gray-500 text-sm">
                                                    No cancelled bookings found.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div className="mt-auto mb-2">
                            <Page
                                paginationPage={paginationPage}
                                nextPage={nextPage}
                                previousPage={previousPage}
                                canPreviousPage={canPreviousPage}
                                canNextPage={canNextPage}
                                pageOptions={pageOptions}
                                screen="unassignedBookings"
                            />
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default CancelledBookings;
